/**
 * Client-side deadlines for Firebase callable functions.
 *
 * WHY THIS EXISTS — the Firebase SDK's own timeout does not cover the whole call.
 * `@firebase/functions` (`callAtURL`) does:
 *
 *     const headers = await makeAuthHeaders(...);   // ← auth + App Check tokens
 *     const failAfterHandle = failAfter(timeout);   // ← timer armed only NOW
 *     await Promise.race([postJSON(...), failAfterHandle.promise, ...]);
 *
 * `makeAuthHeaders` awaits `getContext()`, which awaits the auth token, the
 * messaging token and the App Check token in sequence. Any of those can stall
 * indefinitely — a backgrounded mobile tab, a token refresh on a dead/flapping
 * network, an evicted IndexedDB persistence store, a reCAPTCHA Enterprise fetch
 * that never returns. While it is stalled NO request has been issued and NO
 * timer is running, so the promise never settles: the caller's `finally` never
 * runs, its in-flight guard ref stays `true`, and the button spins forever with
 * no error and nothing to retry.
 *
 * Observed in production 2026-09-03: 批次放行 of 14 筆 from a phone spun forever,
 * while Cloud Run logged ZERO requests for `payment-batchConfirmRelease` in that
 * window — not even a CORS preflight. Nothing had left the browser.
 *
 * TWO LAYERS, deliberately:
 *
 *  1. `warmAuthToken()` bounds the dominant stall (the auth token) BEFORE the
 *     callable is invoked. Once it resolves, the SDK's own `getAuthToken()` hits
 *     the cached token and returns immediately, so the SDK arms its timer
 *     promptly and covers the network phase as designed.
 *  2. `callDeadlineFor()` is a backstop for the phases we cannot pre-warm
 *     (messaging / App Check). It is sized as
 *     `token warm-up + the SDK's own timeout + grace`.
 *
 * Ordering caveat, stated precisely: the two timers have DIFFERENT origins. Ours
 * starts when the callable is invoked; the SDK's starts after `makeAuthHeaders`
 * resolves. So with `M` = header-acquisition time, ours fires at
 * `20s + sdkTimeout` and the SDK's at `M + sdkTimeout` — ours can only pre-empt
 * the SDK when `M > 20s`, i.e. when header acquisition is already pathological.
 * It is NOT unconditionally later, and the margin does not scale with
 * `sdkTimeout`, so the 540 s 差旅 bundle has the same 20 s margin as any other
 * call. In that pre-empted case the outcome is identical anyway
 * (`deadline-exceeded`, and mutations still do not retry).
 *
 * KNOWN GAP — the likeliest real-world stall is App Check, and layer 1 does not
 * cover it. `@firebase/app-check` loads reCAPTCHA Enterprise via
 * `script.onload` with NO `onerror` and NO timeout, so a blocked or failed
 * script leaves `getToken()` pending forever. That matches the observed
 * production symptom exactly (zero requests, not even a preflight). It is
 * bounded here only by layer 2 — a 90 s spinner rather than an infinite one.
 * Note the backend sets `enforceAppCheck: false` on every callable, so the App
 * Check token is optional to the server; shortening or removing that dependency
 * is the real fix and needs a product decision.
 */
import { auth } from '@/boot/firebase';

/** The Firebase callable SDK's own default timeout, when no override is given. */
export const SDK_DEFAULT_TIMEOUT_MS = 70_000;

/**
 * Ceiling on the pre-flight auth-token fetch. Generous enough for a genuine
 * refresh over a slow mobile link, short enough that a stalled token surfaces as
 * an error while the user is still looking at the screen.
 */
export const TOKEN_WARMUP_DEADLINE_MS = 15_000;

/** Headroom over the SDK's own timer so the backstop never fires first. */
export const CALL_DEADLINE_GRACE_MS = 5_000;

/**
 * Raised when a callable attempt never settles. Carries the SDK's own
 * `deadline-exceeded` code so `parseError` shows the standard 操作逾時 message,
 * but it is deliberately NOT treated as transient: the stall is in token
 * acquisition, so an immediate retry stalls the same way and would only make the
 * user wait N× longer before seeing an error.
 */
export class CallTimeoutError extends Error {
  readonly code = 'functions/deadline-exceeded';

  constructor(
    readonly functionName: string,
    readonly deadlineMs: number,
  ) {
    super(
      `Callable "${functionName}" did not settle within ${deadlineMs}ms ` +
        `(stalled before the request was sent).`,
    );
    this.name = 'CallTimeoutError';
  }
}

/** Rejects with {@link CallTimeoutError} if `promise` has not settled in `ms`. */
export function withDeadline<T>(promise: Promise<T>, ms: number, name: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      // Deliberately console.warn, which production KEEPS (vite.config.ts uses
      // terser `pure_funcs` rather than `drop_console`, precisely so incidents
      // that cannot be reproduced locally leave a trail). `name` carries the
      // phase — "(auth init)", "(token)", "(token refresh)" or the bare CF name
      // for the whole-call backstop — so a recurrence identifies its own cause
      // without needing a debugger attached to the device.
      console.warn(`[callDeadline] "${name}" exceeded ${ms}ms with no response.`);
      reject(new CallTimeoutError(name, ms));
    }, ms);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}

/**
 * Backstop deadline for one whole callable attempt. Always strictly greater than
 * the SDK's own timer plus the bounded warm-up, so a legitimately long call
 * (e.g. the 540 s 差旅 bundle) can never be cut short by it.
 */
export function callDeadlineFor(sdkTimeoutMs: number = SDK_DEFAULT_TIMEOUT_MS): number {
  return TOKEN_WARMUP_DEADLINE_MS + sdkTimeoutMs + CALL_DEADLINE_GRACE_MS;
}

/**
 * Resolve the auth token under our own deadline so the SDK's `makeAuthHeaders`
 * reads it from cache and arms its timer without delay.
 *
 * Not forced: this must reuse the cached token when it is still valid, exactly
 * as the SDK would. Forcing a refresh here would add a network round-trip to
 * every single call.
 *
 * CRITICAL — this must NOT introduce a failure mode the SDK does not have.
 * `@firebase/functions` swallows every auth-token error and sends the request
 * with no Authorization header:
 *
 *     async getAuthToken() { ... try { ... } catch (e) { return undefined; } }
 *
 * whereas `getIdToken()` genuinely rejects for `auth/user-token-expired`,
 * `auth/user-disabled` and `auth/network-request-failed`. Letting those
 * propagate broke two real recovery paths: the kiosk's
 * `instanceof AuthError` → redirect-to-login (the callable never reached the
 * CF, so the `unauthenticated` refresh branch never ran), and the kiosk PIN
 * login itself — an UNAUTHENTICATED callable that is the way out of a revoked
 * session, which would have been blocked by that same session's dead token.
 *
 * So: bound the wait, and otherwise behave exactly like the SDK. Only our own
 * deadline is allowed to escape.
 */
export async function warmAuthToken(name: string): Promise<void> {
  try {
    // Wait for persistence restore before reading currentUser. On a cold PWA
    // launch it is null until IndexedDB finishes restoring, so reading it
    // directly made the warm-up a no-op for the first calls after launch —
    // exactly the phone-on-a-flaky-link case this exists for. The SDK's own
    // AuthInterop.getToken awaits `_initializationPromise` for the same reason.
    // Resolves instantly once initialised, so this costs nothing per call.
    await withDeadline(auth.authStateReady(), TOKEN_WARMUP_DEADLINE_MS, `${name} (auth init)`);
    const user = auth.currentUser;
    if (!user) return;
    await withDeadline(user.getIdToken(), TOKEN_WARMUP_DEADLINE_MS, `${name} (token)`);
  } catch (error) {
    if (error instanceof CallTimeoutError) throw error;
    // Matches the SDK: proceed unauthenticated and let the CF answer.
  }
}
